import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

// Dashboard flag for overdue milestone payments (7+ days = flagged).
export const OverduePaymentsCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/invoices/overdue-summary`).then(r => setData(r.data)).catch(() => {}); }, []);
  const toggleHold = async (e, i) => {
    e.stopPropagation();
    if (!i.project_id) return toast.error("No project linked to this invoice");
    try {
      const r = await axios.put(`${API}/projects/${i.project_id}/payment-hold`, { on_hold: !i.payment_hold, reason: `Overdue: ${i.label}` });
      toast.success(r.data.payment_hold ? "Job put on hold (office staff see this on the schedule)" : "Hold removed");
      setData(d => ({ ...d, items: d.items.map(x => x.project_id === i.project_id ? { ...x, payment_hold: r.data.payment_hold } : x) }));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  if (!data || data.count === 0) return null;
  return (
    <Card className="border-red-200 bg-red-50/40" data-testid="overdue-payments-card">
      <CardHeader className="pb-2"><CardTitle className="text-lg font-semibold flex items-center gap-2 text-red-800"><AlertTriangle className="w-5 h-5" /> Overdue payments <span className="text-sm font-normal text-red-700">· {data.count} milestone(s) · {money(data.total)}</span></CardTitle></CardHeader>
      <CardContent className="divide-y">
        {data.items.slice(0, 6).map((i, idx) => (
          <div key={`${i.invoice_id}-${idx}`} className="flex items-center justify-between py-2 gap-3 cursor-pointer hover:bg-white/60 -mx-2 px-2 rounded" onClick={() => navigate(`/invoices/${i.invoice_id}`)} data-testid={`overdue-row-${idx}`}>
            <div className="min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{i.customer_name} · {i.invoice_number}</p><p className="text-xs text-slate-500 truncate">{i.label}</p></div>
            <div className="flex items-center gap-2 shrink-0"><span className="text-sm font-semibold">{money(i.amount)}</span>
              <Badge className={i.days_late >= 7 ? "bg-red-600 text-white" : "bg-amber-100 text-amber-800"}>{i.days_late}d late</Badge>
              {i.escalated && <Badge variant="outline" className="border-red-300 text-red-700">notice sent</Badge>}
              {i.pending_approval && <Badge variant="outline" className="border-amber-300 text-amber-700">awaiting your OK</Badge>}
              {i.project_id && <Button size="sm" variant={i.payment_hold ? "destructive" : "outline"} className="h-7 text-xs" onClick={e => toggleHold(e, i)} data-testid={`hold-toggle-${idx}`}>{i.payment_hold ? <><PlayCircle className="w-3.5 h-3.5 mr-1" /> Release hold</> : <><PauseCircle className="w-3.5 h-3.5 mr-1" /> Hold job</>}</Button>}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
